import { api } from "../api.js";
import { esc, cohortIcons, avatarHtml, displayName, siteLocationLabel, difficultyBadgeClass } from "../utils.js";
import { navigate } from "../app.js";
import { planCardHtml, attachPlanCardHandlers } from "../divePlanCard.js";
import { followButtonHtml, attachFollowHandlers } from "../followButton.js";
import { SEARCH_TYPES, resolveType, syncSearchControl } from "../search.js";

/**
 * Search results for one type at a time.
 *
 * The type lives in the URL rather than in component state, so a result
 * set is linkable and the back button works. The pills below re-run the
 * same query against another type, which is the common next move when
 * the first guess was wrong.
 */
export async function render(container, params, query) {
  const type = resolveType(query?.get("type"));
  const q = (query?.get("q") ?? "").trim();

  // Landing here directly should leave the navbar showing the query that
  // produced the page.
  syncSearchControl({ type, q });

  container.innerHTML = `
    <h1 class="display-heading h4 mb-3">Search</h1>
    <!-- The page carries its own field, not just the navbar one. On a
         phone the navbar is behind the hamburger, and refining a search
         should not mean reopening it. -->
    <form id="search-form" class="d-flex flex-wrap gap-2 mb-3" role="search">
      <select class="form-select" id="search-form-type" aria-label="What to search"
              style="width:auto;">
        ${Object.entries(SEARCH_TYPES)
          .map(
            ([key, meta]) =>
              `<option value="${esc(key)}"${key === type ? " selected" : ""}>${esc(meta.label)}</option>`
          )
          .join("")}
      </select>
      <input type="search" class="form-control" id="search-form-input"
             style="min-width:12rem;max-width:22rem;" placeholder="Search"
             aria-label="Search terms" value="${esc(q)}" />
      <button class="btn btn-primary" type="submit">Search</button>
    </form>
    <ul class="nav nav-pills mb-3 gap-2" id="search-types"></ul>
    <div id="search-results"></div>`;

  /** What the page would search for right now — the field as it stands,
   *  not the term the page happened to load with. */
  function currentTerms() {
    return container.querySelector("#search-form-input").value.trim();
  }

  /** Goes to a search. An empty term is a search too: it means "show me
   *  nothing", and the URL carries that so the back button and a shared
   *  link both behave. */
  function goTo(nextType, nextQ) {
    const next = new URLSearchParams({ type: resolveType(nextType) });
    // Omitted rather than sent empty, so the URL of a cleared search is
    // /search?type=cohorts and not /search?type=cohorts&q=.
    if (nextQ) next.set("q", nextQ);
    navigate(`/search?${next.toString()}`);
  }

  container.querySelector("#search-form").addEventListener("submit", (e) => {
    e.preventDefault();
    // Deliberately no early return on an empty field. Refusing to act
    // left the previous results on screen with an empty box above them,
    // so clearing a search appeared to do nothing at all.
    goTo(container.querySelector("#search-form-type").value, currentTerms());
  });

  const pills = container.querySelector("#search-types");
  // No query means nothing to re-run against another type, and a pill
  // that navigates to /search?q= just produces an empty result set that
  // looks like a failed search.
  if (!q) pills.classList.add("d-none");
  pills.innerHTML = Object.entries(SEARCH_TYPES)
    .map(
      ([key, meta]) => `<li class="nav-item">
        <button class="nav-link ${key === type ? "active" : ""}" data-type="${esc(key)}">
          ${esc(meta.label)}
        </button>
      </li>`
    )
    .join("");
  pills.querySelectorAll("[data-type]").forEach((pill) => {
    pill.addEventListener("click", () => {
      // Read at click time, not captured from the URL at render. Taking
      // it from the URL meant a term the reader had just cleared came
      // back the moment they switched type — and switching back again
      // reinstated it for good.
      goTo(pill.dataset.type, currentTerms());
    });
  });

  const results = container.querySelector("#search-results");
  if (!q) {
    results.innerHTML = `<p class="text-slate mb-0">Enter something to search for.</p>`;
    return;
  }

  results.innerHTML = `<div class="text-center py-4"><div class="spinner-border text-primary"></div></div>`;

  let items;
  try {
    items = await api.get(SEARCH_TYPES[type].path, { q });
  } catch (err) {
    results.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Could not run that search.</div></div>`;
    return;
  }

  if (!Array.isArray(items) || items.length === 0) {
    results.innerHTML = `<div class="dc-empty"><i class="bi bi-search"></i><div>No ${esc(SEARCH_TYPES[type].label.toLowerCase())} match “${esc(q)}”.</div></div>`;
    return;
  }

  switch (type) {
    case "dive-events":
      results.innerHTML = items.map(planCardHtml).join("");
      attachPlanCardHandlers(results);
      break;
    case "dive-sites":
      results.innerHTML = items.map(siteRowHtml).join("");
      bindRows(results, (id) => `/dive-site/${id}`);
      break;
    case "cohorts":
      results.innerHTML = items.map(cohortRowHtml).join("");
      bindRows(results, (id) => `/cohort/${id}`);
      break;
    default:
      results.innerHTML = items.map(diverRowHtml).join("");
      bindRows(results, (id) => `/user/${id}`);
      // Follow straight from the results, which is usually why you were
      // looking for someone.
      attachFollowHandlers(results);
      break;
  }
}

function rowShell(id, body) {
  return `
    <div class="card mb-2 card-dc-clickable" data-result-id="${id}">
      <div class="card-body py-3">${body}</div>
    </div>`;
}

function siteRowHtml(site) {
  const location = siteLocationLabel(site);
  return rowShell(
    site.id,
    `<div class="d-flex justify-content-between align-items-start">
       <div>
         <div class="fw-semibold">${esc(site.name)}</div>
         ${location ? `<div class="text-slate small"><i class="bi bi-geo-alt me-1"></i>${esc(location)}</div>` : ""}
       </div>
       ${site.difficulty ? `<span class="${difficultyBadgeClass(site.difficulty)}">${esc(site.difficulty)}</span>` : ""}
     </div>`
  );
}

function cohortRowHtml(cohort) {
  return rowShell(
    cohort.id,
    `<div class="d-flex justify-content-between align-items-center">
       <div>
         <div class="fw-semibold">${cohortIcons(cohort)}${esc(cohort.name)}</div>
         <div class="text-slate small">${cohort.member_count ?? 0} members</div>
       </div>
       ${cohort.is_member ? `<span class="badge text-bg-light border">Member</span>` : ""}
     </div>`
  );
}

function diverRowHtml(user) {
  const name = displayName(user);
  return rowShell(
    user.id,
    `<div class="d-flex align-items-center gap-2">
       ${avatarHtml(name, { sizePx: 36, fontSize: "0.85rem" })}
       <div class="flex-grow-1">
         <div class="fw-semibold">${esc(name)}</div>
         <div class="text-slate small">@${esc(user.username)}</div>
       </div>
       ${followButtonHtml(user, { extraClass: "" })}
     </div>`
  );
}

/** Rows open their subject, except where an inner control was clicked. */
function bindRows(container, pathFor) {
  container.querySelectorAll("[data-result-id]").forEach((row) => {
    row.addEventListener("click", (e) => {
      if (e.target.closest(".follow-btn")) return;
      navigate(pathFor(row.dataset.resultId));
    });
  });
}
